'use client';

/**
 * Collaborate panel — the workspace's collaboration home. Consolidates:
 *   - who's here now (live participants; jump to where they are),
 *   - inviting / sharing the workspace (moved here from the per-row
 *     Spaces action so it's reachable from the workspace you're in),
 *   - live-view toggles (show cursors / viewports).
 *
 * Presence data crosses the `CanvasYjsProvider` boundary via the
 * module-scoped `presence-bridge` (the rail badge reads the same
 * mirror). Renders inside the left panel like every other view.
 */

import { useState } from 'react';
import { useT } from '@/components/i18n-provider';
import {
  Eye,
  MessageSquarePlus,
  MousePointer2,
  Crosshair,
  Plus,
  UserPlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/components/auth-provider';
import { useWorkspace, useWorkspaceItems } from '@/components/workspace/store';
import {
  resolveItemLabel,
  useLiveContentMeta,
} from '@/components/workspace/item-label';
import { ShareDialog } from '@/components/sharing/share-dialog';
import {
  presenceColorForUserId,
  requestLocate,
  setPresenceSetting,
  useParticipantsMirror,
  usePresenceSettings,
} from '@/components/workspace/presence';
import {
  requestOpenThread,
  requestPlaceComment,
  useCommentThreadsMirror,
} from '@/components/workspace/comments';
import { closeWorkspacePanel } from './panel-events';

function Avatar({
  name,
  color,
  src,
}: {
  name: string;
  color: string;
  src?: string | null;
}) {
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full text-[11px] font-semibold text-white"
      style={{ backgroundColor: color }}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        name.slice(0, 1).toUpperCase()
      )}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}

function ToggleRow({
  icon,
  label,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-foreground/[0.06] text-muted-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-[13px] text-foreground">{label}</span>
      <span
        className={cn(
          'relative h-4 w-7 shrink-0 rounded-full transition-colors',
          checked ? 'bg-primary' : 'bg-foreground/20',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all',
            checked ? 'left-[14px]' : 'left-0.5',
          )}
        />
      </span>
    </button>
  );
}

export function CollaborateView() {
  const t = useT();
  const participants = useParticipantsMirror();
  const settings = usePresenceSettings();
  const threads = useCommentThreadsMirror();
  const { user } = useAuth();
  const workspace = useWorkspace();
  const items = useWorkspaceItems();
  const [shareOpen, setShareOpen] = useState(false);

  const openThreads = threads.filter((t) => !t.resolved);

  const startComment = () => {
    requestPlaceComment();
    // Clear the panel so the click-to-place lands on the canvas.
    closeWorkspacePanel('left');
  };
  const openThread = (id: string) => {
    requestOpenThread(id);
    closeWorkspacePanel('left');
  };

  // "in <app>" names the peer's app live-first through item-label.ts, the
  // rail's rule. It read the snapshot stored in the shared item (title
  // first, unlike every other surface), so a peer in the renamed Revent app
  // showed as "in Revent" (the 2026-09-13 browser pass). Read-only.
  const live = useLiveContentMeta();
  const itemTitle = (id: string | null): string | null => {
    if (!id) return null;
    const it = items.find((i) => i.id === id);
    if (!it) return null;
    return resolveItemLabel(it, live, it.type);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Invite / share — the workspace's access lives with its presence. */}
      <div className="px-3 pt-2 pb-1">
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-medium text-background transition-colors hover:bg-foreground/90"
        >
          <UserPlus className="h-3.5 w-3.5" /> {t('workspace.panels.invitePeople')}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        <SectionLabel>
          Here now{participants.length > 0 ? ` · ${participants.length + 1}` : ''}
        </SectionLabel>

        {user ? (
          <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
            <Avatar
              name={user.username}
              color={presenceColorForUserId(user.id)}
              src={user.avatarUrl}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] text-foreground">
                {user.displayName || user.username}{' '}
                <span className="text-muted-foreground">(you)</span>
              </span>
            </span>
          </div>
        ) : null}

        {participants.map((p) => {
          const where = itemTitle(p.focusItemId);
          return (
            <div
              key={p.user.peerId}
              className="group flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-accent/50"
            >
              <Avatar
                name={p.user.username}
                color={p.user.color}
                src={p.user.avatarUrl}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-foreground">
                  {p.user.username}
                </span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {where ? `in ${where}` : 'online'}
                </span>
              </span>
              <button
                type="button"
                onClick={() => requestLocate(p.user.peerId)}
                title={`Jump to ${p.user.username}`}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-colors hover:bg-accent hover:text-foreground group-hover:opacity-100"
              >
                <Crosshair className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}

        {participants.length === 0 ? (
          <p className="px-2 pb-1 pt-0.5 text-[11px] leading-snug text-muted-foreground">
            You&apos;re the only one here. {t('workspace.panels.invitePeople')} and you&apos;ll see
            their cursors and which app they&apos;re working in, live.
          </p>
        ) : null}

        <div className="flex items-center justify-between px-2 pt-3 pb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Comments{openThreads.length > 0 ? ` · ${openThreads.length}` : ''}
          </span>
          <button
            type="button"
            onClick={startComment}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-primary hover:bg-accent/50"
          >
            <Plus className="h-3 w-3" /> {t('workspace.panels.addComment')}
          </button>
        </div>
        {threads.length === 0 ? (
          <button
            type="button"
            onClick={startComment}
            className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[11px] text-muted-foreground transition-colors hover:bg-accent/50"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-foreground/[0.06]">
              <MessageSquarePlus className="h-4 w-4" />
            </span>
            {t('workspace.panels.leaveCommentHint')}
          </button>
        ) : (
          threads.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => openThread(t.id)}
              className="flex w-full items-start gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
            >
              <span
                className={cn(
                  'mt-0.5 flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white',
                  t.resolved ? 'bg-muted-foreground/70' : 'bg-amber-500',
                )}
              >
                {t.count}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-foreground">
                  {t.preview || 'Comment'}
                </span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {t.authorName}
                  {t.resolved ? ' · resolved' : ''}
                </span>
              </span>
            </button>
          ))
        )}

        <SectionLabel>{t('workspace.panels.liveView')}</SectionLabel>
        <ToggleRow
          icon={<MousePointer2 className="h-4 w-4" />}
          label={t('workspace.panels.showCursors')}
          checked={settings.showCursors}
          onChange={(v) => setPresenceSetting('showCursors', v)}
        />
        <ToggleRow
          icon={<Eye className="h-4 w-4" />}
          label={t('workspace.panels.showViewports')}
          checked={settings.showViewports}
          onChange={(v) => setPresenceSetting('showViewports', v)}
        />
      </div>

      {shareOpen && workspace?.id ? (
        <ShareDialog
          kind="workspace"
          workspaceId={workspace.id}
          onClose={() => setShareOpen(false)}
        />
      ) : null}
    </div>
  );
}
